// Cinder Husk (replaces `bomber`) — short hunched biped, matte black shell split open at the chest around an oversized
// orange-red reactor held in a copper cage, with two small gripping arms. Wind-up (a pulse in game): the shell rears
// back and the claws twitch up while the reactor flares.
// Elite (Ember Vessel): heavy reactor cage (thicker bars, extra hoop, shoulder plates), bigger core and two back vents.
import * as THREE from 'three/webgpu';
import { ArtBuilder, at, EnemyArt, limb, PAL, plate } from './kit';
import { ball, compassDip, leg, noSkateSwing, V3 } from './parts_spec';

const STRIDE = 4.0, HIP = 0.56;
const SW = noSkateSwing(HIP, STRIDE);
const SHELL = 0x24232b, SHELL_LIGHT = 0x3a3842, CORE = 0xff5a1e, HOT = 0xffb040;

function build(elite: boolean): ReturnType<ArtBuilder['build']> {
  const b = new ArtBuilder();
  const sw = elite ? noSkateSwing(HIP * 1.7, STRIDE) : SW;
  for (const s of [-1, 1]) {
    leg(b, {
      hip: [s * 0.2, HIP, -0.06], knee: [s * 0.27, 0.33, 0.09], ankle: [s * 0.23, 0.08, -0.04],
      rThigh: 0.085, rShin: 0.07, foot: [0.17, 0.08, 0.24], toe: 0.06,
      thigh: SHELL, shin: SHELL_LIGHT, kneeCol: PAL.copperDark, footCol: SHELL,
      swing: sw, sphase: s > 0 ? 0 : Math.PI, noKnee: true,
    });
  }
  // upper body rears back about the hips on the telegraph
  const U = { pivot: [0, HIP, -0.06] as V3, raise: -0.12 };
  const cr = elite ? 0.25 : 0.21, cz = 0.3, cy = 0.92;
  // hunched shell: a back dome tipped forward, open at the chest where the reactor sits
  b.add(at(new THREE.SphereGeometry(0.46, 10, 6, Math.PI * 0.72, Math.PI * 1.56), 0, cy, -0.08, 0.35, 0, 0, [1.05, 0.95, 1]), { color: SHELL, ...U });
  b.add(ball(0.3, [0, cy, -0.02], [1, 1, 1], 5, 3), { color: 0x120d0d, ...U }); // dark cavity behind the core
  b.add(limb([0, 0.62, -0.06], [0, 0.78, 0], 0.2, 0.3, 6), { color: SHELL_LIGHT, ...U }); // belly
  // dorsal ridge plates
  for (let i = 0; i < 3; i++) b.add(at(plate(0.09, 0.14, 0.2, 0.03, 1), 0, 1.33 - i * 0.14, -0.08 - i * 0.16, -0.5 - i * 0.35), { color: SHELL_LIGHT, ...U });
  // the reactor + copper cage
  b.add(at(new THREE.IcosahedronGeometry(cr, 1), 0, cy, cz), { color: CORE, glow: true, ...U });
  b.add(at(new THREE.IcosahedronGeometry(cr * 0.55, 0), 0, cy, cz + cr * 0.55), { color: HOT, glow: true, ...U });
  const bar = elite ? 0.032 : 0.02, R = cr + 0.05;
  b.add(at(new THREE.TorusGeometry(R, bar, 3, 10), 0, cy, cz, Math.PI / 2), { color: PAL.copper, ...U });
  if (elite) b.add(at(new THREE.TorusGeometry(R * 0.8, bar, 3, 10), 0, cy + R * 0.6, cz - 0.02, Math.PI / 2), { color: PAL.copper, ...U });
  for (const x of [-0.11, 0.11]) {
    // cage bars: over the top, down the front of the core, back under it
    const zf = cz + Math.sqrt(Math.max(0, R * R - x * x)) + 0.02;
    b.add(limb([x, cy + R + 0.04, cz - 0.08], [x * 1.05, cy + 0.02, zf], bar, bar, 4), { color: PAL.copper, ...U });
    b.add(limb([x * 1.05, cy + 0.02, zf], [x, cy - R - 0.03, cz - 0.08], bar, bar, 4), { color: PAL.copper, ...U });
  }
  // low head peeking over the reactor
  b.add(ball(0.13, [0, 1.25, 0.22], [1.1, 0.8, 1.05], 6, 4), { color: SHELL, ...U });
  b.add(at(plate(0.24, 0.05, 0.12, 0.02, 1), 0, 1.31, 0.27, 0.25), { color: PAL.copperDark, ...U });
  for (const s of [-1, 1]) b.add(at(plate(0.06, 0.02, 0.02, 0.006, 1), s * 0.055, 1.24, 0.35, 0, s * 0.3), { color: HOT, glow: true, ...U });
  if (elite) {
    for (const s of [-1, 1]) {
      b.add(at(plate(0.26, 0.09, 0.3, 0.03, 1), s * 0.33, 1.15, 0.0, 0, 0, s * -0.55), { color: PAL.copper, ...U });
      // back vents (chimneys) with ember mouths
      b.add(limb([s * 0.16, 1.15, -0.3], [s * 0.2, 1.48, -0.42], 0.07, 0.06, 4), { color: PAL.copperDark, ...U });
      b.add(at(new THREE.CylinderGeometry(0.05, 0.05, 0.03, 4), s * 0.2, 1.495, -0.425, -0.35), { color: CORE, glow: true, ...U });
    }
  }
  // two small gripping arms under the cage, claws reaching forward
  for (const s of [-1, 1]) {
    const sh: V3 = [s * 0.3, 1.0, 0.1], el: V3 = [s * 0.36, 0.8, 0.22], h: V3 = [s * 0.27, 0.72, 0.42];
    const o = { pivot: sh, swing: 0.3, sphase: s > 0 ? Math.PI : 0, raise: -0.55 };
    b.add(limb(sh, el, 0.05, 0.045, 4), { color: SHELL_LIGHT, ...o });
    b.add(limb(el, h, 0.045, 0.04, 4), { color: SHELL, ...o });
    for (const c of [-1, 1]) b.add(at(new THREE.ConeGeometry(0.025, 0.12, 4), h[0] + c * 0.03, h[1], h[2] + 0.05, Math.PI / 2 + 0.3, 0, c * 0.2), { color: PAL.copper, ...o });
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Cinder Husk', build, stride: STRIDE, bob: compassDip(HIP, SW) * 0.85 };
